import React, { useEffect, useState } from "react";
import {
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  EyeOutlined,
  SettingOutlined,
  FolderOutlined,
  UserOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  List,
  notification,
  Space,
  Tooltip,
  Typography,
  Tag,
} from "antd";
import {
  deleteNotification,
  getNotifications,
  retrieveNotificationSettings,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  updateNotificationSettings,
} from "../../redux/actions/notificationActions";
import {
  Notification,
  NotificationData,
  UserNotificationSettings,
} from "../../redux/interfaces/notifications.types";
import { API_HOST } from "../../constants/common";
import { useAppSelector } from "../../redux/hooks";

const { Text } = Typography;

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
}) => {
  // Extract data from notification
  const data = notification.data || null;
  const folderNumber =
    data?.folder_number ||
    (notification.message.match(/folio (\d+)/) || [])[1] ||
    "";

  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }

    // Navigate to the folder if we have an ID
    if (data?.folder_id) {
      window.location.href = `/folders/${data.folder_id}`;
    } else if (notification.folder_id) {
      window.location.href = `/folders/${notification.folder_id}`;
    } else if (
      notification.object_type === "folder" &&
      notification.object_id
    ) {
      window.location.href = `/folders/${notification.object_id}`;
    }
  };

  return (
    <List.Item
      className={`notification-item ${notification.read ? "notification-read" : "notification-unread"}`}
      style={{
        borderLeft: `4px solid ${notification.read ? "#e8e8e8" : "#1890ff"}`,
        background: notification.read
          ? "transparent"
          : "rgba(24, 144, 255, 0.1)",
        transition: "all 0.3s",
        cursor: "pointer",
        padding: "10px 16px",
      }}
      onClick={handleClick}
      actions={[
        <Space>
          {!notification.read && (
            <Tooltip title="Marcar como leído">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsRead(notification.id);
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Eliminar">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(notification.id);
              }}
            />
          </Tooltip>
        </Space>,
      ]}
    >
      <List.Item.Meta
        title={
          <Text strong={!notification.read} style={{ fontSize: "14px" }}>
            {notification.title}
            {folderNumber && (
              <Tag color="blue" style={{ marginLeft: 8, fontSize: "12px" }}>
                {folderNumber}
              </Tag>
            )}
          </Text>
        }
        description={
          <>
            {/* Show a condensed version of the message or structured data if available */}
            {data?.folder_number ? (
              <CompactNotificationContent data={data} />
            ) : (
              <div
                style={{
                  fontSize: "13px",
                  color: notification.read ? "#8c8c8c" : "#333",
                }}
              >
                {getShortMessage(notification.message)}
              </div>
            )}
            <div style={{ color: "#8c8c8c", fontSize: "12px", marginTop: 4 }}>
              {notification.timeSince}
            </div>
          </>
        }
      />
    </List.Item>
  );
};

// A helper component to show structured notification data in a compact way
const CompactNotificationContent: React.FC<{
  data: NotificationData | null;
}> = ({ data }) => {
  if (!data) return null;

  return (
    <div style={{ fontSize: "13px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          color: "#666",
        }}
      >
        {data && (
          <div>
            <div>
              {data.subfolder && (
                <span>
                  <FolderOutlined /> {data.subfolder}
                </span>
              )}
            </div>
            <div>
              {data.advisor_name && (
                <span>
                  <TeamOutlined /> {data.advisor_name}
                </span>
              )}
            </div>
            <div>
              {data.tenant_name && (
                <span>
                  <UserOutlined /> {data.tenant_name}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper to truncate/format notification messages
const getShortMessage = (message: string): string => {
  if (!message) return "";

  // Remove markdown-style formatting
  const cleanMsg = message.replace(/\*/g, "");

  // Truncate if too long
  if (cleanMsg.length > 100) {
    return cleanMsg.substring(0, 100) + "...";
  }

  return cleanMsg;
};

const NotificationSettings: React.FC<{
  settings: UserNotificationSettings;
  onSave: (values: UserNotificationSettings) => void;
}> = ({ settings, onSave }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue(settings);
  }, [settings, form]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onSave}
      initialValues={settings}
    >
      <Form.Item
        name="slack_user_id"
        label="ID de usuario de Slack"
        extra="Tu ID de usuario de Slack para @menciones en grupos de notificaciones."
      >
        <Input placeholder="Enter your Slack user ID" />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit">
          Guardar
        </Button>
      </Form.Item>
    </Form>
  );
};

const NotificationBell: React.FC = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeView, setActiveView] = useState<"all" | "unread" | "settings">(
    "all",
  );
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationSettings, setNotificationSettings] =
    useState<UserNotificationSettings>({
      status_in_app: true,
      status_slack: false,
      summary_in_app: true,
      summary_slack: false,
      slack_user_id: "",
    });
  const [loading, setLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const { userInfo } = useAppSelector((state) => state.auth);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await getNotifications();
      setNotifications(data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const data = await retrieveNotificationSettings();
      if (data) {
        setNotificationSettings(data);
      }
    } catch (error) {
      console.error("Error fetching notification settings:", error);
    }
  };

  const handleBellClick = () => {
    fetchNotifications();
    setDrawerVisible(true);
    setActiveView("all");
  };

  const toggleSettings = () => {
    setActiveView(activeView === "settings" ? "all" : "settings");
  };

  const handleMarkAsRead = async (notificationId: string) => {
    const is_read = await markNotificationAsRead(notificationId);
    if (is_read) {
      setNotifications((prevNotifications) =>
        prevNotifications.map((item) =>
          item.id === notificationId ? { ...item, read: true } : item,
        ),
      );
    }
  };

  const handleMarkAllAsRead = async () => {
    const unreadIds = notifications
      .filter((item) => !item.read)
      .map((item) => item.id);

    if (unreadIds.length === 0) return;

    const success = await markAllNotificationsAsRead(unreadIds);
    if (success) {
      setNotifications((prevNotifications) =>
        prevNotifications.map((item) => ({ ...item, read: true })),
      );
    }
  };

  const handleDelete = async (notificationId: string) => {
    const success = await deleteNotification(notificationId);
    if (success) {
      setNotifications((prevNotifications) =>
        prevNotifications.filter((item) => item.id !== notificationId),
      );
    }
  };

  const handleSaveSettings = async (values: UserNotificationSettings) => {
    const updated = await updateNotificationSettings(values);
    if (updated) {
      setNotificationSettings(updated);
      api.success({
        message: "Configuración guardada",
        description:
          "Tu configuración de notificaciones ha sido actualizada correctamente.",
      });
    }
  };

  const openNotification = (notification: Notification) => {
    // Extract data for a better formatted toast
    const data = notification.data || null;
    const folderNumber =
      data?.folder_number ||
      (notification.message.match(/folio (\d+)/) || [])[1] ||
      "";

    api.info({
      message: notification.title,
      description: (
        <div>
          {folderNumber && (
            <div style={{ marginBottom: 8 }}>
              <FolderOutlined style={{ marginRight: 4 }} />
              <strong>Expediente:</strong> {folderNumber}
            </div>
          )}
          {data?.subfolder && (
            <div style={{ marginBottom: 8 }}>
              <FolderOutlined style={{ marginRight: 4 }} />
              {data.subfolder}
            </div>
          )}
          {data?.advisor_name && (
            <div style={{ marginBottom: 8 }}>
              <TeamOutlined style={{ marginRight: 4 }} />
              <strong>Asesor:</strong> {data.advisor_name}
            </div>
          )}
          {data?.tenant_name && (
            <div style={{ marginBottom: 8 }}>
              <UserOutlined style={{ marginRight: 4 }} />
              <strong>Inquilino:</strong> {data.tenant_name}
            </div>
          )}
          {!data?.folder_number &&
            !data?.advisor_name &&
            !data?.tenant_name && (
              <div>{getShortMessage(notification.message)}</div>
            )}
          {(data?.folder_id ||
            notification.folder_id ||
            (notification.object_type === "folder" &&
              notification.object_id)) && (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                const folderId =
                  data?.folder_id ||
                  notification.folder_id ||
                  notification.object_id;
                window.location.href = `/folders/${folderId}`;
              }}
              style={{ marginTop: 8 }}
            >
              Ver expediente
            </Button>
          )}
        </div>
      ),
      placement: "topRight",
      duration: 5,
      onClick: () => {
        // Mark as read when clicked
        handleMarkAsRead(notification.id);

        // Navigate to folder if applicable
        const folderId =
          data?.folder_id ||
          notification.folder_id ||
          (notification.object_type === "folder"
            ? notification.object_id
            : null);

        if (folderId) {
          window.location.href = `/folders/${folderId}`;
        }
      },
    });
  };

  useEffect(() => {
    // Fetch notifications when component mounts
    if (userInfo?.id) {
      fetchNotifications();
      fetchNotificationSettings();
    }

    if (!userInfo?.id || !notificationSettings.status_in_app) {
      return;
    }

    // Setup SSE connection with reconnection logic
    let eventSource: EventSource | null = null;
    let reconnectAttempt = 0;
    const maxReconnectAttempts = 5;
    const baseReconnectDelay = 1000; // Start with 1 second

    const connectSSE = () => {
      const sseUrl = `${API_HOST}/notifications/sse/${userInfo.id}/`;
      console.log(
        `Connecting to SSE: ${sseUrl} (attempt ${reconnectAttempt + 1})`,
      );

      try {
        // Close existing connection if any
        if (eventSource) {
          eventSource.close();
        }

        eventSource = new EventSource(sseUrl);

        eventSource.onopen = () => {
          console.info("SSE connection established");
          reconnectAttempt = 0; // Reset attempts on successful connection
        };

        eventSource.onmessage = (event) => {
          // Skip heartbeats and system messages
          if (
            !event.data ||
            event.data === "{}" ||
            event.data.includes("heartbeat") ||
            event.data.includes("connection established") ||
            event.data.includes("Subscribed")
          ) {
            return;
          }

          try {
            const data = JSON.parse(event.data);
            console.info("Notification received:", data);

            // Process the incoming notification
            const newNotification: Notification = {
              id: data.id,
              notification_type: data.type,
              level: data.level,
              title: data.title,
              message: data.message,
              read: false,
              created_at: data.created_at,
              timeSince: "just now",
              object_type: data.object_type,
              object_id: data.object_id,
              data: data.data, // Include structured data if available
              folder_id: data.data?.folder_id, // Extract folder_id for convenience
            };

            // Show popup notification
            openNotification(newNotification);

            // Add to the notification list
            setNotifications((prev) => [newNotification, ...prev]);
          } catch (error) {
            console.error("Error processing notification:", error);
          }
        };

        eventSource.onerror = (error) => {
          console.error("SSE connection error:", error);
          eventSource?.close();

          // Try to reconnect with exponential backoff
          if (reconnectAttempt < maxReconnectAttempts) {
            const delay = baseReconnectDelay * Math.pow(2, reconnectAttempt);
            reconnectAttempt++;
            console.info(
              `Reconnecting in ${delay}ms (attempt ${reconnectAttempt}/${maxReconnectAttempts})`,
            );

            setTimeout(connectSSE, delay);
          } else {
            console.error("Max reconnection attempts reached, giving up");
          }
        };
      } catch (error) {
        console.error("Error setting up EventSource:", error);
      }
    };

    // Initial connection
    connectSSE();

    // Cleanup on unmount
    return () => {
      if (eventSource) {
        console.info("Closing SSE connection");
        eventSource.close();
      }
    };
  }, [userInfo, notificationSettings.status_in_app]);

  const getFilteredNotifications = () => {
    switch (activeView) {
      case "unread":
        return notifications.filter((item) => !item.read);
      default:
        return notifications;
    }
  };

  // Calculate unread count from notifications array
  const unreadCount = notifications.filter((item) => !item.read).length;

  return (
    <>
      {contextHolder}
      <Badge count={unreadCount} overflowCount={99}>
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: "20px" }} />}
          onClick={handleBellClick}
          size="large"
        />
      </Badge>

      <Drawer
        title="Notificaciones"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={360}
        extra={
          <Space>
            {activeView !== "settings" && unreadCount > 0 && (
              <Tooltip title="Marcar todas como leídas">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  onClick={handleMarkAllAsRead}
                />
              </Tooltip>
            )}
            <Tooltip title="Configuración">
              <Button
                type="text"
                icon={<SettingOutlined />}
                onClick={toggleSettings}
                className={activeView === "settings" ? "ant-btn-primary" : ""}
              />
            </Tooltip>
          </Space>
        }
      >
        {activeView === "settings" ? (
          <NotificationSettings
            settings={notificationSettings}
            onSave={handleSaveSettings}
          />
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <Button
                type={activeView === "all" ? "primary" : "default"}
                onClick={() => setActiveView("all")}
                style={{ marginRight: 8 }}
              >
                Todas
              </Button>
              <Button
                type={activeView === "unread" ? "primary" : "default"}
                onClick={() => setActiveView("unread")}
                disabled={unreadCount === 0}
              >
                Sin Leer ({unreadCount})
              </Button>
            </div>

            <List
              dataSource={getFilteredNotifications()}
              loading={loading}
              renderItem={(item) => (
                <NotificationItem
                  notification={item}
                  onMarkAsRead={handleMarkAsRead}
                  onDelete={handleDelete}
                />
              )}
              locale={{
                emptyText: (
                  <Empty
                    description={
                      activeView === "unread"
                        ? "No hay notificaciones sin leer"
                        : "No hay notificaciones"
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </>
        )}
      </Drawer>
    </>
  );
};

export default NotificationBell;
